import { useCallback, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useParsedQuery } from '@/hooks/useParsedQuery'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { useCoMingleShipperScope } from '@/components/system/useCoMingleShipperScope'
import { type ShipperScope, withCoMingle, shipperScopeOf } from '@/components/system/shipperScope'
import { lockShipperName } from '@/components/system/shipperScopeOptions'
import { flushSync } from 'react-dom'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { useMenuOnSession } from '@/components/layout/MainMenuOnSessionProvider'
import { defaultValues, InventoryHistoryForm } from '@/pages/inventory/inventoryHistory/components/InventoryHistoryForm'
import { InventoryHistoryTable } from '@/pages/inventory/inventoryHistory/components/InventoryHistoryTable'
import { InventoryHistoryItem, InventoryHistoryReqParams } from '@/services/inventory/inventory.types'
import { useInventoryHistory, useInventoryHistoryDetail } from '@/services/inventory/useInventoryQuries'
import { toInventoryHistoryQuery } from '@/services/inventory/apis'
import { InventoryHistoryDetailTable } from '@/pages/inventory/inventoryHistory/components/InventoryHistoryDetailTable'
import { PageLayout } from '@/components/layout/PageLayout'
import { VStack } from '@musinsa/mwms-ds'
import TitleSection from '@/components/content/TitleSection'
import ResizableContents from '@/components/content/ResizableContents'
import { toSelectOptions, useGetCombos } from '@/services/combo/useComboQueries'

function InventoryHistory() {
  const { ready, coMingle } = useCoMingleShipperScope()
  // scope 는 진입 시점 값이다. coMingle 은 훅이 진입 시점 값으로 고정하고 Content 는 ready 뒤에만 마운트되므로,
  // Content 의 initialParams·onSearch 가 deps [] 로 scope 를 읽어도 Content 가 살아 있는 동안 바뀌지 않는다.
  const scope = useMemo(() => withCoMingle(shipperScopeOf(AuthStorage.user.get()), coMingle), [coMingle])
  return ready ? <InventoryHistoryContent scope={scope} /> : null
}

const InventoryHistoryContent = ({ scope }: { scope: ShipperScope }) => {
  const { reload } = useMenuOnSession()
  const { parsedQs, replaceQuery } = useParsedQuery<InventoryHistoryReqParams>()
  // 화주 잠금(LOGISPRD-14653) — 통합 운영 화주 외 계정은 복원(URL)·조회 조건의 화주(sprNm)를 고를 수 있는 화주로 맞춘다. 재고통합 ON 이면 29CM·INOOINT 는 MUSINSA 도 고른다(LOGISPRD-15652).
  const initialParams = useMemo(() => lockShipperName(defaultValues(parsedQs, scope), scope), [])
  const form = useForm<InventoryHistoryReqParams>({
    defaultValues: initialParams,
  })
  const [detailItem, setDetailItem] = useState<InventoryHistoryItem | null>(null)
  // 마지막으로 검색한 조건 — 조회와 엑셀이 같이 쓴다. 폼 값은 검색 뒤에도 바뀔 수 있어 폼 값으로 조회하지 않는다.
  // 검색 시 조건을 URL 에 실으므로(replaceQuery) 다시 마운트돼도 폼 초기값과 같은 조건이다.
  const [searchedParams, setSearchedParams] = useState<InventoryHistoryReqParams>(initialParams)
  const { data: combos } = useGetCombos(['DOWNLOAD_CD'])
  const downloadReasonOptions = useMemo(() => toSelectOptions(combos?.DOWNLOAD_CD), [combos])

  const { data, isFetching, refetch } = useInventoryHistory(searchedParams)
  const {
    data: detailData,
    isFetching: isDetailFetching,
    refetch: detailRefetch,
  } = useInventoryHistoryDetail({
    sprNm: detailItem?.shipperCode ?? '',
    clsMonth: detailItem?.clsMonth ?? '',
  })

  const onSearch = useCallback((formValues: InventoryHistoryReqParams) => {
    const values = lockShipperName(formValues, scope)
    MenuStorage.menuOnSession.setSearch(values)
    flushSync(() => {
      setSearchedParams(values)
      replaceQuery(values)
    })
    reload()
    refetch()
  }, [])

  const onDetailSearch = (value: InventoryHistoryItem) => {
    flushSync(() => setDetailItem(value))
    detailRefetch()
  }

  return (
    <FormProvider {...form}>
      <PageLayout gap={16}>
        <VStack gap={12}>
          <TitleSection variant="legacy" title="재고 수불 내역" />
          <InventoryHistoryForm onSearch={onSearch} scope={scope} />
        </VStack>
        <ResizableContents
          list={[
            {
              content: (
                <InventoryHistoryTable
                  data={data}
                  isLoading={isFetching}
                  downloadReasonOptions={downloadReasonOptions}
                  excelQuery={toInventoryHistoryQuery(searchedParams)}
                  onDetailSearch={onDetailSearch}
                />
              ),
            },
            {
              content: (
                <InventoryHistoryDetailTable
                  data={detailData}
                  isLoading={isDetailFetching}
                  downloadReasonOptions={downloadReasonOptions}
                  excelQuery={detailItem ? { sprNm: detailItem.shipperCode, clsMonth: detailItem.clsMonth } : undefined}
                />
              ),
            },
          ]}
        />
      </PageLayout>
    </FormProvider>
  )
}

export default InventoryHistory
